import React from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@emotion/react';
import { styled } from '@mui/material/styles';

/**
 * RunningHorse — animated "thinking" indicator for the AI assistant.
 *
 * Uses the 🐎 glyph (unmistakably a horse at any size) with a pure-CSS gallop
 * bob + a little scrolling dust trail. No animation library. Respects
 * `prefers-reduced-motion` (freezes the gallop + hides the dust, keeps the
 * label). The glyph is easy to swap for a custom brand SVG later.
 *
 * The 🐎 glyph faces LEFT by default, which reads as "running backward". We
 * mirror it with scaleX(-1) so it faces RIGHT (forward) into the dust that
 * trails off to the left behind its hooves. scaleX is baked into both the
 * gallop keyframes and the static (reduced-motion) transform.
 */

// Gallop: face right (scaleX(-1)) and bob up/down with a slight rock, like a
// horse running in place.
const gallop = keyframes`
  0%   { transform: scaleX(-1) translateY(0)    rotate(3deg);  }
  25%  { transform: scaleX(-1) translateY(-4px) rotate(-2deg); }
  50%  { transform: scaleX(-1) translateY(-6px) rotate(2deg);  }
  75%  { transform: scaleX(-1) translateY(-2px) rotate(-3deg); }
  100% { transform: scaleX(-1) translateY(0)    rotate(3deg);  }
`;

// Dust puffs scroll backward (leftward) from under the hooves and fade.
const dust = keyframes`
  0%   { transform: translateX(0)     scale(1);   opacity: 0.55; }
  100% { transform: translateX(-16px) scale(0.3); opacity: 0;    }
`;

const Horse = styled('span', { shouldForwardProp: (p) => p !== 'fs' })<{ fs: number }>(
  ({ fs }) => ({
    fontSize: fs,
    lineHeight: 1,
    display: 'inline-block',
    transformOrigin: '50% 85%',
    // Face right (forward). The gallop keyframes re-apply scaleX(-1); this base
    // value keeps it facing right when reduced-motion freezes the animation.
    transform: 'scaleX(-1)',
    animation: `${gallop} 0.5s ease-in-out infinite`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  }),
);

const Dust = styled('span', { shouldForwardProp: (p) => p !== 'delay' })<{ delay: number }>(
  ({ delay }) => ({
    position: 'absolute',
    bottom: 1,
    left: 0,
    width: 6,
    height: 2,
    borderRadius: 2,
    backgroundColor: '#CD853F',
    animation: `${dust} 0.5s linear ${delay}s infinite`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
  }),
);

export interface RunningHorseProps {
  /** Font size (px) of the horse glyph. Default 30. */
  size?: number;
  /** Optional label rendered beside the horse. Default "Thinking…" */
  label?: string;
}

export const RunningHorse: React.FC<RunningHorseProps> = ({
  size = 30,
  label = 'Thinking…',
}) => {
  return (
    <Box
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
      role="status"
      aria-label={label || 'Thinking'}
    >
      <Box
        sx={{
          position: 'relative',
          width: size + 8,
          height: size + 2,
          display: 'inline-flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
        }}
      >
        <Dust delay={0} />
        <Dust delay={0.15} sx={{ left: 9, bottom: 4 }} />
        <Dust delay={0.3} sx={{ left: 4, bottom: 7, width: 4 }} />
        <Horse fs={size} aria-hidden="true">
          🐎
        </Horse>
      </Box>
      {label && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ userSelect: 'none', whiteSpace: 'nowrap' }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
};

export default RunningHorse;
